import type { FC, ReactNode } from 'react'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef
} from 'react'
import { useRouter } from 'next/router'
import { gsap, ScrollTrigger, prefersReducedMotion } from '@/lib/gsap'
import { LogoMark } from '@/components/generals/logo'

type TransitionState = 'idle' | 'closing' | 'waiting' | 'opening'

interface PageTransitionValue {
    navigate: (href: string) => void
}

const PageTransitionContext = createContext<PageTransitionValue | null>(null)

export const usePageTransition = (): PageTransitionValue => {
    const value = useContext(PageTransitionContext)
    const router = useRouter()

    return value ?? {
        navigate: (href: string) => {
            void router.push(href)
        }
    }
}

interface PageTransitionProviderProps {
    children: ReactNode
}

export const PageTransitionProvider: FC<PageTransitionProviderProps> = ({ children }): ReactNode => {
    const router = useRouter()
    const doorRef = useRef<HTMLDivElement>(null)
    const markRef = useRef<HTMLDivElement>(null)
    const stateRef = useRef<TransitionState>('idle')

    const open = useCallback(() => {
        const door = doorRef.current

        if (!door || stateRef.current === 'opening' || stateRef.current === 'idle') return

        stateRef.current = 'opening'

        gsap.timeline({
            onComplete: () => {
                gsap.set(door, { autoAlpha: 0, pointerEvents: 'none' })
                stateRef.current = 'idle'
                ScrollTrigger.refresh()
            }
        })
            .to(markRef.current, { autoAlpha: 0, y: -16, duration: 0.3, ease: 'power2.in' })
            .to(door, { yPercent: -100, duration: 0.95, ease: 'expo.inOut' }, '<0.08')
    }, [])

    const navigate = useCallback((href: string) => {
        const door = doorRef.current

        if (!door || prefersReducedMotion()) {
            void router.push(href)

            return
        }

        if (stateRef.current !== 'idle') return

        stateRef.current = 'closing'
        document.documentElement.style.setProperty('--enter-delay', '360ms')
        router.prefetch(href).catch(() => undefined)

        gsap.killTweensOf([door, markRef.current])
        gsap.set(door, { autoAlpha: 1, y: 0, yPercent: -100, pointerEvents: 'auto' })
        gsap.set(markRef.current, { autoAlpha: 0, y: 16 })

        gsap.timeline()
            .to(door, { yPercent: 0, duration: 0.62, ease: 'expo.inOut' })
            .to(markRef.current, { autoAlpha: 1, y: 0, duration: 0.4, ease: 'power3.out' }, '-=0.2')
            .add(() => {
                stateRef.current = 'waiting'

                router.push(href)
                    .then(() => open())
                    .catch(() => open())
            })
    }, [router, open])

    useEffect(() => {
        const handleDone = () => {
            if (stateRef.current === 'waiting') open()
        }

        router.events.on('routeChangeComplete', handleDone)
        router.events.on('routeChangeError', handleDone)

        return () => {
            router.events.off('routeChangeComplete', handleDone)
            router.events.off('routeChangeError', handleDone)
        }
    }, [router, open])

    const value = useMemo(() => ({ navigate }), [navigate])

    return (
        <PageTransitionContext.Provider value={value}>
            {children}

            <div
                ref={doorRef}
                aria-hidden='true'
                className='pointer-events-none fixed inset-0 z-[100] overflow-hidden'
                style={{ visibility: 'hidden', opacity: 0 }}
            >
                <div className='absolute inset-0 bg-ink' />
                <div className='door-grooves absolute inset-0' />
                <div className='bg-noise absolute inset-0 opacity-[0.07] mix-blend-overlay' />
                <div className='absolute inset-x-0 bottom-0 h-2 bg-ember' />

                <div ref={markRef} className='absolute inset-0 flex flex-col items-center justify-center gap-4'>
                    <LogoMark className='size-14' inverted />

                    <span className='font-mono text-[11px] uppercase tracking-[0.35em] text-cream/50'>
                        Liftline
                    </span>
                </div>
            </div>
        </PageTransitionContext.Provider>
    )
}
